import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@mui/material";
import { Eye, MoreVertical } from "lucide-react";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../hooks";
import { setCaseDetailModal } from "../../../../states/slices/globalReducer";

const columnHelper = createColumnHelper<any>();

const ActionMenu = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="flex items-center text-[#D0D5DD] hover:text-[#667085] focus:outline-none">
        <MoreVertical className="w-5 h-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
          <div className="px-1 py-1 flex flex-col gap-y-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate("/president/case-folders/view")}
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#027A48]" : "text-[#344054]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  Open
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => dispatch(setCaseDetailModal(true))}
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#027A48]" : "text-[#344054]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  Assign case
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#027A48]" : "text-[#344054]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  Recall case
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export const PresidentCaseFolderColumns = [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  columnHelper.accessor("date", {
    header: "Date",
    cell: (info) => <span className="text-[#667085]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("title", {
    header: "Title",
    cell: (info) => (
      <span
        className="text-[#101828] font-medium max-w-[260px] truncate block"
        title={info.getValue()}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("appeal_number", {
    header: "Appeal number",
    cell: (info) => <span className="text-[#101828]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("details", {
    header: "Details",
    cell: () => (
      <div className="flex items-center gap-1 text-[#101828] cursor-pointer hover:text-[#00B074]">
        <span>View</span>
        <Eye className="w-4 h-4" />
      </div>
    ),
  }),
  columnHelper.accessor("assigned_panel", {
    header: "Assigned Panel",
    cell: (info) => <span className="text-[#667085]">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: "action",
    header: "Action",
    cell: () => <ActionMenu />,
  }),
];

type RecusedActionMenuProps = {
  onViewRequest: () => void;
};

const ActionMenuRecused = ({ onViewRequest }: RecusedActionMenuProps) => {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="flex items-center text-[#D0D5DD] hover:text-[#667085] focus:outline-none">
        <MoreVertical className="w-5 h-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
          <div className="px-1 py-1 flex flex-col gap-y-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={onViewRequest}
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#027A48]" : "text-[#344054]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  View request
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export const PresidentRecusedCaseFolderColumns = [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: (info) => (
      <span className="text-[#101828]">{info.row.index + 1}</span>
    ),
  }),
  columnHelper.accessor("date", {
    header: "Date",
    cell: (info) => <span className="text-[#667085]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("title", {
    header: "Case Title",
    cell: (info) => (
      <span
        className="text-[#101828] font-medium max-w-[260px] truncate block"
        title={info.getValue()}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("appeal_number", {
    header: "Appeal number",
    cell: (info) => <span className="text-[#101828]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("details", {
    header: "Details",
    cell: () => (
      <div className="flex items-center gap-1 text-[#101828] cursor-pointer hover:text-[#00B074]">
        <span>View</span>
        <Eye className="w-4 h-4" />
      </div>
    ),
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => (
      <span className="text-[#D97706] font-medium">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("assigned_judge", {
    header: "Assigned Judge",
    cell: (info) => <span className="text-[#101828]">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: "action",
    header: "Action",
    cell: (info) => (
      <ActionMenuRecused onViewRequest={info.row.original.onViewRequest} />
    ),
  }),
];
